import { useEffect, useRef, useState } from 'react';
import { usePurchaseOrder } from '@/hooks/use-purchase-orders';
import { formatDateTime } from '@/lib/utils';
import {
  describeFilter,
  filterByLocation,
  readLocationFilterFromHash,
  sortByLocation,
  type SaleOrderLine,
} from './sale-order-locations';
import { isZeroQty, ZERO_QTY_ROW_CLASS } from './zero-qty';
import { ZeroQtyTag } from './zero-qty-tag';

interface Props {
  orderId: string;
}

/**
 * Print view for a warehouse Sale Order — a pick list in walking order.
 * Building / Aisle filters arrive via the hash query (?building=…&aisle=…) from
 * the Sale Order page, so staff can print one area at a time. "Save as PDF"
 * in the browser print dialog produces the PDF, as with Purchase Order print.
 */
export function SaleOrderPrintPage({ orderId }: Props) {
  const { data, isLoading } = usePurchaseOrder(orderId);
  const order = data?.data;
  // Read once per load — the Sale Order page opens a separate named tab per
  // filter combination, so the query never changes under a mounted page.
  const [filter] = useState(readLocationFilterFromHash);

  // Same guard as Purchase Order print: fire the print dialog once per load.
  const hasPrintedRef = useRef(false);
  useEffect(() => {
    if (order && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [order]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }
  if (!order) {
    return <div className="flex h-screen items-center justify-center">Sale order not found.</div>;
  }

  const allItems = sortByLocation(order.items as SaleOrderLine[]);
  const items = filterByLocation(allItems, filter);
  const filterLabel = describeFilter(filter);

  return (
    <div className="mx-auto max-w-4xl p-8 print:max-w-none print:p-4">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sale Order #{order.orderNumber}</h1>
          <p className="text-sm text-muted-foreground">Printed: {formatDateTime(new Date().toISOString())}</p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-3 gap-6 rounded-lg border border-border p-4">
        <div>
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Destination</h3>
          <p className="mt-1 font-medium">{order.location?.name || order.locationName || '—'}</p>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Ordered By</h3>
          <p className="mt-1 font-medium">{order.createdByName || 'System'}</p>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Status</h3>
          <p className="mt-1 font-medium capitalize">{order.status}</p>
        </div>
      </div>

      {filterLabel && (
        <p className="mb-3 text-sm">
          <span className="font-semibold">Filtered:</span> {filterLabel} — {items.length} of {allItems.length} items
        </p>
      )}

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-border">
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Product</th>
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Building</th>
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Aisle</th>
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Bin/Bay</th>
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Unit</th>
            <th className="py-2 text-right text-xs font-semibold uppercase text-muted-foreground">Qty</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                No line items match the selected building and aisle.
              </td>
            </tr>
          )}
          {items.map((item) => (
            <tr
              key={item.id}
              className={`border-b border-border break-inside-avoid ${isZeroQty(item.quantity) ? ZERO_QTY_ROW_CLASS : ''}`}
            >
              <td className="py-2">
                <div className="font-medium">
                  {item.productName}
                  {isZeroQty(item.quantity) && <ZeroQtyTag />}
                </div>
                {item.productBarcode && <div className="text-xs text-muted-foreground">{item.productBarcode}</div>}
              </td>
              <td className="py-2 text-sm">{item.productBuilding || '—'}</td>
              <td className="py-2 text-sm">{item.productAisle || '—'}</td>
              <td className="py-2 text-sm">{item.productBin || '—'}</td>
              <td className="py-2 text-sm">{item.unitOfMeasure}</td>
              <td className="py-2 text-right text-sm">{Number(item.quantity)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-8 print:hidden">
        <button
          onClick={() => window.print()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Print
        </button>
        <button
          onClick={() => window.close()}
          className="ml-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Close
        </button>
      </div>
    </div>
  );
}
